import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { Users, Shield, Pencil, Eye, Trash, UserCircle, Crown, ArrowsClockwise, Plus, UserPlus } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { da } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleConfig = {
  admin: { 
    label: "Administrator", 
    description: "Fuld adgang til alle funktioner",
    color: "bg-purple-100 text-purple-700 border-purple-200",
    icon: Crown
  },
  editor: { 
    label: "Redaktør", 
    description: "Kan oprette og redigere indhold",
    color: "bg-blue-100 text-blue-700 border-blue-200",
    icon: Pencil
  },
  viewer: { 
    label: "Læser", 
    description: "Kan kun se indhold",
    color: "bg-slate-100 text-slate-700 border-slate-200",
    icon: Eye
  }
};

const RoleBadge = ({ role }) => {
  const config = roleConfig[role] || roleConfig.viewer;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${config.color}`}>
      <Icon className="w-3.5 h-3.5" weight="bold" />
      {config.label}
    </span>
  );
};

const defaultUserForm = {
  name: "",
  email: "",
  role: "editor"
};

export const UsersPage = ({ user, setUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userForm, setUserForm] = useState(defaultUserForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/users`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else if (response.status === 403) {
        toast.error("Du har ikke adgang til denne side");
      }
    } catch (error) {
      toast.error("Kunne ikke hente brugere");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setUserForm(defaultUserForm);
    setSelectedUser(null);
    setCreateDialogOpen(true);
  };

  const handleOpenEdit = (targetUser) => {
    setSelectedUser(targetUser);
    setUserForm({
      name: targetUser.name || "",
      email: targetUser.email || "",
      role: targetUser.role || "editor"
    });
    setEditDialogOpen(true);
  };

  const handleCreateUser = async () => {
    if (!userForm.name || !userForm.email) {
      toast.error("Udfyld venligst navn og email");
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API}/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(userForm)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setCreateDialogOpen(false);
        setUserForm(defaultUserForm);
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Kunne ikke oprette bruger");
      }
    } catch (error) {
      toast.error("Der opstod en fejl");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser || !userForm.name || !userForm.email) {
      toast.error("Udfyld venligst navn og email");
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API}/users/${selectedUser.user_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(userForm)
      });
      
      if (response.ok) {
        toast.success("Bruger opdateret");
        setEditDialogOpen(false);
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Kunne ikke opdatere bruger");
      }
    } catch (error) {
      toast.error("Der opstod en fejl");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      const response = await fetch(`${API}/users/${selectedUser.user_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success("Bruger slettet");
        setDeleteDialogOpen(false);
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Kunne ikke slette bruger");
      }
    } catch (error) {
      toast.error("Der opstod en fejl");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return format(new Date(dateStr), "d. MMM yyyy", { locale: da });
    } catch {
      return "-";
    }
  };

  // Check if current user is admin
  if (user?.role !== 'admin') {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="p-6 md:p-8">
          <Card className="card-default border-amber-200 bg-amber-50/30">
            <CardContent className="p-6 text-center">
              <Shield className="w-12 h-12 text-amber-500 mx-auto mb-4" weight="duotone" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Adgang nægtet</h2>
              <p className="text-slate-600">Kun administratorer har adgang til brugeradministration.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="p-6 md:p-8 flex items-center justify-center">
          <ArrowsClockwise className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </Layout>
    );
  }

  const UserFormFields = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Navn *</Label>
        <Input
          id="name"
          placeholder="Fulde navn"
          value={userForm.name}
          onChange={(e) => setUserForm(prev => ({ ...prev, name: e.target.value }))}
          data-testid="user-name-input"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="email">Email *</Label>
        <Input
          id="email"
          type="email"
          placeholder="bruger@gardinbussen.org"
          value={userForm.email}
          onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
          data-testid="user-email-input"
        />
        <p className="text-xs text-slate-500">Skal være fra @gardinbussen.org domænet</p>
      </div>
      <div className="space-y-2">
        <Label>Rolle</Label>
        <Select 
          value={userForm.role} 
          onValueChange={(value) => setUserForm(prev => ({ ...prev, role: value }))}
        >
          <SelectTrigger data-testid="user-role-select">
            <SelectValue placeholder="Vælg rolle" />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(roleConfig).map(([key, config]) => {
              const Icon = config.icon;
              return (
                <SelectItem key={key} value={key}>
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    <span>{config.label}</span>
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        {userForm.role && (
          <p className="text-xs text-slate-500">{roleConfig[userForm.role]?.description}</p>
        )}
      </div>
    </div>
  );

  return (
    <Layout user={user} setUser={setUser}>
      <div className="p-6 md:p-8" data-testid="users-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Brugeradministration
            </h1>
            <p className="text-slate-600">Administrer brugere og deres roller</p>
          </div>
          <Button onClick={handleOpenCreate} className="btn-primary" data-testid="add-user-btn">
            <UserPlus className="w-4 h-4 mr-2" />
            Tilføj bruger
          </Button>
        </div>

        {/* Role explanation */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {Object.entries(roleConfig).map(([key, config]) => {
            const Icon = config.icon;
            return (
              <Card key={key} className="card-default">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${config.color.replace('text-', 'bg-').split(' ')[0]}`}>
                      <Icon className="w-5 h-5" weight="bold" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{config.label}</h3>
                      <p className="text-xs text-slate-500">{config.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Users list */}
        <Card className="card-default">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-600" weight="duotone" />
              Brugere ({users.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {users.length === 0 ? (
              <div className="text-center py-8">
                <UserCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" weight="duotone" />
                <p className="text-slate-500 mb-4">Ingen brugere fundet</p>
                <Button onClick={handleOpenCreate} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Opret den første bruger
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {users.map((targetUser, index) => (
                  <motion.div
                    key={targetUser.user_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      targetUser.user_id === user.user_id 
                        ? 'border-indigo-200 bg-indigo-50/30' 
                        : 'border-slate-200 hover:bg-slate-50'
                    } transition-colors`}
                  >
                    <div className="flex items-center gap-4">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={targetUser.avatar_url} />
                        <AvatarFallback className="bg-indigo-100 text-indigo-600">
                          {targetUser.name?.charAt(0) || targetUser.email?.charAt(0) || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">{targetUser.name || 'Unavngivet'}</span>
                          {targetUser.user_id === user.user_id && (
                            <Badge variant="outline" className="text-xs">Dig</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{targetUser.email}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right mr-4 hidden sm:block">
                        <p className="text-xs text-slate-500">Oprettet</p>
                        <p className="text-sm text-slate-700">{formatDate(targetUser.created_at)}</p>
                      </div>
                      <RoleBadge role={targetUser.role} />
                      
                      {targetUser.user_id !== user.user_id && (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEdit(targetUser)}
                            data-testid={`edit-user-${targetUser.user_id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => {
                              setSelectedUser(targetUser);
                              setDeleteDialogOpen(true);
                            }}
                            data-testid={`delete-user-${targetUser.user_id}`}
                          >
                            <Trash className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create User Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-indigo-600" />
                Tilføj ny bruger
              </DialogTitle>
              <DialogDescription>
                Opret en ny bruger med adgang til admin panelet
              </DialogDescription>
            </DialogHeader>
            
            <UserFormFields />
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Annuller
              </Button>
              <Button onClick={handleCreateUser} disabled={saving} className="btn-primary">
                {saving ? "Opretter..." : "Opret bruger"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil className="w-5 h-5 text-indigo-600" />
                Rediger bruger
              </DialogTitle>
              <DialogDescription>
                Opdater oplysninger for {selectedUser?.name || selectedUser?.email}
              </DialogDescription>
            </DialogHeader>
            
            <UserFormFields />
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Annuller
              </Button>
              <Button onClick={handleUpdateUser} disabled={saving} className="btn-primary">
                {saving ? "Gemmer..." : "Gem ændringer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Slet bruger?</AlertDialogTitle>
              <AlertDialogDescription>
                Er du sikker på at du vil slette {selectedUser?.name || selectedUser?.email}? 
                Denne handling kan ikke fortrydes.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuller</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteUser}
                className="bg-red-500 hover:bg-red-600"
              >
                Slet bruger
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};
